import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ExternalLink, Globe, Map, ChevronRight } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sitemap() {
  const [stats, setStats] = useState({ total: 0, content: 0 });
  
  useEffect(() => {
    // Fetch stats
    fetch(`${BACKEND_URL}/api/stats/homepage`)
      .then(res => res.json())
      .then(data => {
        setStats({
          total: (data.content_count || 0) + 12, // content + static pages
          content: data.content_count || 0
        });
      })
      .catch(console.error);
  }, []);

  const staticPages = [
    { path: '/', title: 'Home', description: 'Your personalized skin & hair care intelligence platform' },
    { path: '/daily-drop', title: 'Daily Drop', description: 'Fresh beauty content delivered daily' },
    { path: '/deep-dives', title: 'Deep Dives', description: 'In-depth articles and guides' },
    { path: '/video-vault', title: 'Video Vault', description: 'Beauty tutorials and expert videos' },
    { path: '/knowledge', title: 'Knowledge Corner', description: 'Educational skincare and haircare content' },
    { path: '/products', title: 'Products', description: 'Browse and discover beauty products' },
    { path: '/quiz', title: 'Quiz', description: 'Test your beauty knowledge' },
    { path: '/leaderboard', title: 'Leaderboard', description: 'Top SkinPoints earners' },
    { path: '/ai-advisor', title: 'AI Advisor', description: 'Get personalized beauty advice' },
    { path: '/deals', title: 'Smart Discounts', description: 'Exclusive product deals' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 py-12 pt-24">
        <div className="container max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Map className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-4xl font-semibold mb-4">Sitemap</h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Explore all pages on SkinUse. Find articles, videos, products, and more.
            </p>
            
            {/* Stats */}
            <div className="flex justify-center gap-8 mt-8">
              <div className="text-center">
                <p className="font-serif text-3xl font-semibold text-gold">{stats.total}</p>
                <p className="text-xs text-muted-foreground mt-1">Total Pages</p>
              </div>
              <div className="text-center">
                <p className="font-serif text-3xl font-semibold text-gold">{stats.content}</p>
                <p className="text-xs text-muted-foreground mt-1">Articles & Videos</p>
              </div>
            </div>
          </div>

          {/* XML Sitemap Link */}
          <div className="bg-gold/5 border border-gold/20 rounded-lg p-6 mb-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <FileText className="w-8 h-8 text-gold" />
                <div>
                  <h3 className="font-semibold">XML Sitemap</h3>
                  <p className="text-sm text-muted-foreground">For search engines and developers</p>
                </div>
              </div>
              <a 
                href="/api/sitemap.xml" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-black font-medium rounded-sm hover:bg-gold/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View XML
              </a>
            </div>
          </div>

          {/* Main Pages */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-semibold mb-6 flex items-center gap-2">
              <Globe className="w-5 h-5 text-gold" />
              Main Pages
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {staticPages.map((page) => (
                <Link 
                  key={page.path} 
                  to={page.path}
                  className="group p-4 bg-card rounded-lg border border-border hover:border-gold/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium group-hover:text-gold transition-colors">{page.title}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{page.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Content Section */}
          <section className="mb-10">
            <h2 className="font-serif text-2xl font-semibold mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-gold" />
              Content ({stats.content} pages)
            </h2>
            <p className="text-muted-foreground mb-4">
              All articles, guides, and videos are listed in our XML sitemap for search engines.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              <Link to="/deep-dives" className="p-4 bg-card rounded-lg border border-border hover:border-gold/50 transition-colors text-center">
                <p className="font-medium">Articles</p>
                <p className="text-xs text-muted-foreground mt-1">In-depth guides</p>
              </Link>
              <Link to="/video-vault" className="p-4 bg-card rounded-lg border border-border hover:border-gold/50 transition-colors text-center">
                <p className="font-medium">Videos</p>
                <p className="text-xs text-muted-foreground mt-1">Tutorials & tips</p>
              </Link>
              <Link to="/knowledge" className="p-4 bg-card rounded-lg border border-border hover:border-gold/50 transition-colors text-center">
                <p className="font-medium">Knowledge</p>
                <p className="text-xs text-muted-foreground mt-1">Educational content</p>
              </Link>
            </div>
          </section>

          {/* Legal */}
          <section>
            <h2 className="font-serif text-2xl font-semibold mb-6">Legal</h2>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-muted-foreground hover:text-gold transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-gold transition-colors">
                Terms of Service
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
